import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import authenticatedDataverseService from '../services/authenticatedDataverseService';
import scheduleGeneratorService from '../services/scheduleGeneratorService';
import { consolidatedTrendToWorkbookData, trendToWorkbookData, trendHubRollup, sessionStatusComparison } from '../utils/registrationTrendReport';

const RegistrationReport = ({ permissions = {}, roleChecking }) => {
  const { accounts } = useMsal();
  const account = accounts[0];

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showLocationMapping, setShowLocationMapping] = useState(false);
  const [dateStart, setDateStart] = useState('2023-01-01');
  const [dateEnd, setDateEnd] = useState('2025-12-31');
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState(null);

  // CEO-facing registration reports are restricted to the IT Admin group.
  const isAdmin = permissions.isITAdmin === true;

  // Course × Location 3-year trend (current year YTD vs two prior full years) →
  // Excel, for the CEO's gain/loss recovery analysis.
  const exportCourseLocationTrend = async () => {
    setTrendLoading(true);
    setTrendError(null);
    try {
      const { default: XLSX } = await import('xlsx-js-style');
      const result = await authenticatedDataverseService.getRegistrationsByCourseLocation();
      if (!result.success) {
        setTrendError(result.error || 'Failed to generate the course/location trend report');
        return;
      }

      let priceInfo = { success: false, priceField: null, byGuid: {}, byName: {}, error: null };
      try {
        priceInfo = await scheduleGeneratorService.getCoursePrices();
      } catch (priceError) {
        console.error('Failed to load mi_course prices for registration report:', priceError);
        priceInfo.error = priceError.message;
      }
      const byGuid = priceInfo.byGuid || {};
      const byName = priceInfo.byName || {};
      result.rows.forEach(row => {
        const normalizedName = String(row.course || '').trim().toLowerCase().replace(/\s+/g, ' ');
        if (row.courseGuid && Object.prototype.hasOwnProperty.call(byGuid, row.courseGuid)) {
          row.courseCost = byGuid[row.courseGuid];
        } else if (Object.prototype.hasOwnProperty.call(byName, normalizedName)) {
          row.courseCost = byName[normalizedName];
        } else {
          row.courseCost = null;
        }
      });

      // Two metrics: registrations (attendees) and sessions (classes held).
      const consolidated = consolidatedTrendToWorkbookData(result);
      const regMain = trendToWorkbookData(result, 'registrations');
      const regHub = trendHubRollup(result, 'registrations');
      const sesMain = trendToWorkbookData(result, 'sessions');
      const sesHub = trendHubRollup(result, 'sessions');
      const statusComparison = sessionStatusComparison(result);

      // Apply number formats: integer counts and red-negative deltas.
      const applyFormats = (sheet, deltaCols, currencyCols) => {
        const delta = new Set(deltaCols || []);
        const currency = new Set(currencyCols || []);
        const range = XLSX.utils.decode_range(sheet['!ref']);
        for (let r = range.s.r; r <= range.e.r; r++) {
          for (let c = range.s.c; c <= range.e.c; c++) {
            const cell = sheet[XLSX.utils.encode_cell({ r, c })];
            if (!cell || cell.t !== 'n') continue;
            if (currency.has(c)) cell.z = '$#,##0.00';
            else if (delta.has(c)) cell.z = '#,##0;[Red]-#,##0';
            else cell.z = '#,##0';
          }
        }
      };

      // Build a formatted worksheet from a { aoa, colWidths, autofilterRef?, deltaCols } bundle.
      const buildSheet = (data) => {
        const sheet = XLSX.utils.aoa_to_sheet(data.aoa);
        sheet['!cols'] = data.colWidths;
        if (data.autofilterRef) sheet['!autofilter'] = { ref: XLSX.utils.encode_range(data.autofilterRef) };
        applyFormats(sheet, data.deltaCols, data.currencyCols);
        const range = XLSX.utils.decode_range(sheet['!ref']);
        [range.s.r, range.e.r].forEach(r => {
          for (let c = range.s.c; c <= range.e.c; c++) {
            const cell = sheet[XLSX.utils.encode_cell({ r, c })];
            if (cell) cell.s = { font: { bold: true } };
          }
        });
        return sheet;
      };

      const yrs = result.years;
      const currentYr = yrs[yrs.length - 1];       // YTD year
      const fullYrs = yrs.slice(0, -1);            // full calendar years
      const lastFull = fullYrs[fullYrs.length - 1];
      const prevFull = fullYrs[fullYrs.length - 2];
      const yearsLabel = yrs
        .map(y => (y === currentYr ? `${y} (YTD through ${result.asOf})` : `${y} (full)`))
        .join(' · ');
      const notes = [
        [`Course × Location — ${yrs.length}-Year Trend (Registrations & Sessions)`],
        [],
        ['Generated', new Date().toLocaleString('en-GB')],
        ['Years compared', yearsLabel],
        ['Current year basis', `${currentYr} is YEAR-TO-DATE (Jan 1 → ${result.asOf}); ${fullYrs.join(', ')} are FULL calendar years`],
        ['Apples-to-apples', `Numeric change columns compare consecutive FULL years only (e.g. ${lastFull} vs ${prevFull}); the ${currentYr} YTD column has NO change column because a partial year vs a full year is not comparable. Course/location rows are sorted by Course A-Z`],
        [],
        ['Registrations', 'Number of attendees (registration records) - the primary attendance-trend metric'],
        ['Sessions', 'Number of classes held - pair with registrations to see students-per-session (schedule efficiency)'],
        [],
        ['Counted by', 'Session (class) start date'],
        ['Course identity', 'mi_course catalog (session-name variants collapsed)'],
        ['Bundle rules', 'Basic Training: count children, skip parent · Other bundles: count parent, skip children'],
        ['Excluded statuses', 'No Show, Cancelled by MI, Cancelled, Dropped'],
        ['Excluded courses', 'Yacht Charter (removed per CEO) · Basic Training (broken out into its child classes; parent skipped) · 24 Hour Medical Care Provider · 4 Hour Personal Safety & Social Responsibility · Able Seaman- Historical · TWIC Card · Untitled · Merchant Mariner Credential · MAMA Examination Retake · Captain\'s Course (6-pack/100Ton) (Classroom) · GOV All (6) Courses · High Voltage Safety Historical · Proficiency in Survival Craft (Lifeboatman) · Radar Observer Unlimited (RADU) · Ratings Forming Part of an Engineering Watch (RFPEW) · Visual Communications (Flashing Light) · Vessel Security Officer (VSO) · Upgrade OUPV to Master 100 Tons (Online) · auto-detected session names (compact codes such as MSCRR011323SD or Course Name M/D/YYYY City, ST)'],
        ['Excluded locations', '"MAMA" (known test/junk location)'],
        ['Hub source', 'INTERIM: derived from city/campus in src/utils/hubLocation.js until the hub is a field in Power Apps. Every location rolls up to the NEAREST hub (Guam & Pita → VA, Online → CA), and any session on the Maritime - East campus rolls up to VA even when its location has no geographic signal, so "Unclassified" should be empty unless the location text has no geographic signal'],
        ['Course Cost source', priceInfo.success ? `Dataverse mi_course.${priceInfo.priceField}; matched by course GUID, then normalized course name` : `Dataverse mi_course price unavailable; Course Cost cells left blank${priceInfo.error ? ` (${priceInfo.error})` : ''}`],
        ['Session status tracking', `One row per course/campus/hub/location, with Basic Training counting the parent only (children omitted). 2025 includes Jan-Dec; 2026 includes the full scheduled year through ${result.sessionStatusTrackingEnd}. Scheduled = every session placed on the calendar; Cancelled = the subset marked cr38c_coursecancelled; Held = the non-cancelled subset whose start time has passed as of ${result.asOf}. Total Scheduled does not add the overlapping Cancelled and Held columns`],
        ['Total registrations counted', result.totalRegistrations],
        ['Total sessions counted', result.totalSessions],
        ['Sessions analyzed', result.sessionsAnalyzed],
        [],
        ['Locations that could NOT be placed in a hub (shown as "Unclassified" — add cues to src/utils/hubLocation.js):'],
        ...(result.unclassified.length === 0
          ? [['(none — every location mapped to a hub)']]
          : result.unclassified.map(name => [name]))
      ];
      const notesSheet = XLSX.utils.aoa_to_sheet(notes);
      notesSheet['!cols'] = [{ wch: 30 }, { wch: 80 }];

      const wb = XLSX.utils.book_new();
      // Sheet names kept <= 31 chars (Excel limit).
      XLSX.utils.book_append_sheet(wb, buildSheet(consolidated), 'Consolidated Course-Loc');
      XLSX.utils.book_append_sheet(wb, buildSheet(regMain), 'Registrations by Course-Loc');
      XLSX.utils.book_append_sheet(wb, buildSheet(regHub), 'Registrations by Hub');
      XLSX.utils.book_append_sheet(wb, buildSheet(sesMain), 'Sessions by Course-Loc');
      XLSX.utils.book_append_sheet(wb, buildSheet(sesHub), 'Sessions by Hub');
      XLSX.utils.book_append_sheet(wb, buildSheet(statusComparison), 'Session Status Comparison');
      XLSX.utils.book_append_sheet(wb, notesSheet, 'Notes');
      XLSX.writeFile(wb, `course-location-trend-${result.asOf}.xlsx`);
    } catch (err) {
      console.error('Course/location trend export failed:', err);
      setTrendError(err.message);
    } finally {
      setTrendLoading(false);
    }
  };

  const generateReport = async () => {
    setLoading(true);
    setError(null);
    setReportData(null);
    try {
      const result = await authenticatedDataverseService.getRegistrationReport({
        startDate: `${dateStart}T00:00:00Z`,
        endDate: `${dateEnd}T23:59:59Z`
      });
      if (result.success) {
        setReportData(result);
      } else {
        setError(result.error || 'Failed to generate report');
      }
    } catch (err) {
      console.error('Report generation failed:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!reportData) return;
    const { data } = reportData;
    const regions = ['Washington', 'Bootcamp (WA)', 'South', 'MI East', 'Bootcamp (MI East)', 'Online'];
    const years = reportData?.years || [2023, 2024, 2025];

    let csv = 'Region,' + years.join(',') + ',Total\n';
    regions.forEach(region => {
      const rowTotal = years.reduce((sum, year) => sum + (data[year]?.[region] || 0), 0);
      csv += `${region},${years.map(y => data[y]?.[region] || 0).join(',')},${rowTotal}\n`;
    });
    // Total row
    const totalRow = years.map(y => data[y]?.Total || 0);
    csv += `Total,${totalRow.join(',')},${totalRow.reduce((a, b) => a + b, 0)}\n`;

    // Add location mapping section
    if (reportData.locationRegionMap) {
      csv += '\n\nLocation,Region,Campus\n';
      Object.entries(reportData.locationRegionMap)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([location, info]) => {
          csv += `"${location}",${info.region},"${info.campus || ''}"\n`;
        });
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `registration-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (roleChecking) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontSize: '16px', color: '#6b7280' }}>Checking permissions...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '32px', maxWidth: '480px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#991b1b', marginBottom: '8px' }}>Access Denied</h2>
          <p style={{ color: '#dc2626' }}>You must be an IT administrator to view this page.</p>
        </div>
      </div>
    );
  }

  const regions = ['Washington', 'Bootcamp (WA)', 'South', 'MI East', 'Bootcamp (MI East)', 'Online'];
  const defaultYears = [2023, 2024, 2025];
  const years = reportData?.years || defaultYears;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Header */}
      <header style={{ backgroundColor: '#1e293b', borderBottom: '3px solid #3b82f6', padding: '16px 32px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#ffffff', margin: 0 }}>Registration Report</h1>
            <p style={{ fontSize: '14px', color: '#94a3b8', margin: '4px 0 0' }}>Registration counts by region</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <a href="/" style={{ fontSize: '14px', color: '#93c5fd', textDecoration: 'none' }}>← Back to Calendar</a>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '14px', color: '#94a3b8', margin: 0 }}>Signed in as</p>
              <p style={{ fontSize: '14px', fontWeight: '500', color: '#ffffff', margin: 0 }}>{account?.name}</p>
            </div>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px' }}>
        {/* Description */}
        <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e40af', margin: '0 0 8px' }}>Report Criteria</h3>
          <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#1e3a5f', lineHeight: '1.8' }}>
            <li>Counts registrations with an assigned student only</li>
            <li>Filters out: Drops, No Shows, Cancelled by MI</li>
            <li><strong>Bundle Handling — Basic Training:</strong> Counts each child course individually (parent not counted)</li>
            <li><strong>Bundle Handling — All Others (Bootcamp, MSC Gov Vessels, etc.):</strong> Counts the parent bundle only (children not counted)</li>
            <li><strong>Washington</strong>: All registrations with a WA-based location (excluding Bootcamp)</li>
            <li><strong>Bootcamp (WA)</strong>: Bootcamp sessions at WA locations</li>
            <li><strong>South</strong>: MI West campus registrations (excluding Online and Washington)</li>
            <li><strong>MI East</strong>: Registrations at Maritime Institute – East locations (excluding Bootcamp)</li>
            <li><strong>Bootcamp (MI East)</strong>: Bootcamp sessions at MI East locations</li>
            <li><strong>Online</strong>: Online locations</li>
          </ul>
        </div>

        {/* Date Range Selection */}
        <div style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Date Range:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '13px', color: '#6b7280' }}>From:</label>
              <input
                type="date"
                value={dateStart}
                onChange={(e) => setDateStart(e.target.value)}
                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', color: '#374151' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '13px', color: '#6b7280' }}>To:</label>
              <input
                type="date"
                value={dateEnd}
                onChange={(e) => setDateEnd(e.target.value)}
                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', color: '#374151' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', color: '#6b7280', marginRight: '4px' }}>Presets:</span>
            {[
              { label: 'Annual (2023–2025)', start: '2023-01-01', end: '2025-12-31' },
              { label: '2023', start: '2023-01-01', end: '2023-12-31' },
              { label: '2024', start: '2024-01-01', end: '2024-12-31' },
              { label: '2025', start: '2025-01-01', end: '2025-12-31' },
            ].map(p => (
              <button
                key={p.label}
                onClick={() => { setDateStart(p.start); setDateEnd(p.end); }}
                style={{
                  padding: '4px 10px', fontSize: '12px', fontWeight: '500', borderRadius: '6px', border: '1px solid #d1d5db', cursor: 'pointer',
                  backgroundColor: dateStart === p.start && dateEnd === p.end ? '#2563eb' : '#f9fafb',
                  color: dateStart === p.start && dateEnd === p.end ? '#ffffff' : '#374151'
                }}
              >{p.label}</button>
            ))}
            <span style={{ fontSize: '13px', color: '#d1d5db', margin: '0 4px' }}>|</span>
            <span style={{ fontSize: '13px', color: '#6b7280', marginRight: '4px' }}>Quarterly:</span>
            {[
              { label: 'Q1', sm: '01-01', em: '03-31' },
              { label: 'Q2', sm: '04-01', em: '06-30' },
              { label: 'Q3', sm: '07-01', em: '09-30' },
              { label: 'Q4', sm: '10-01', em: '12-31' },
            ].map(q => {
              const currentYear = new Date().getFullYear();
              const qStart = `${currentYear}-${q.sm}`;
              const qEnd = `${currentYear}-${q.em}`;
              return (
                <button
                  key={q.label}
                  onClick={() => { setDateStart(qStart); setDateEnd(qEnd); }}
                  style={{
                    padding: '4px 10px', fontSize: '12px', fontWeight: '500', borderRadius: '6px', border: '1px solid #d1d5db', cursor: 'pointer',
                    backgroundColor: dateStart === qStart && dateEnd === qEnd ? '#059669' : '#f9fafb',
                    color: dateStart === qStart && dateEnd === qEnd ? '#ffffff' : '#374151'
                  }}
                >{q.label} {currentYear}</button>
              );
            })}
          </div>
        </div>

        {/* Generate Button */}
        <div style={{ marginBottom: '24px', display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button
            onClick={generateReport}
            disabled={loading}
            style={{
              backgroundColor: loading ? '#9ca3af' : '#2563eb',
              color: '#ffffff',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '16px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Generating Report...' : 'Generate Report'}
          </button>
          {reportData && (
            <button
              onClick={exportCSV}
              style={{
                backgroundColor: '#059669',
                color: '#ffffff',
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Export CSV
            </button>
          )}
          <button
            onClick={exportCourseLocationTrend}
            disabled={trendLoading}
            title="Registrations by course × location, current year YTD vs the two prior full years (Excel)"
            style={{
              backgroundColor: trendLoading ? '#9ca3af' : '#7c3aed',
              color: '#ffffff',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '16px',
              fontWeight: '600',
              cursor: trendLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {trendLoading ? 'Building Excel…' : 'Course × Location Trend (Excel)'}
          </button>
        </div>

        {trendError && (
          <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '16px', marginBottom: '24px', color: '#991b1b' }}>
            Course × Location trend export failed: {trendError}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '48px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <p style={{ fontSize: '18px', color: '#374151', fontWeight: '600' }}>Generating Registration Report...</p>
            <p style={{ fontSize: '14px', color: '#6b7280' }}>This may take a minute as it queries all registrations for 2023–2025.</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
            <p style={{ color: '#dc2626', margin: 0 }}>Error: {error}</p>
          </div>
        )}

        {/* Results */}
        {reportData && (
          <>
            {/* Summary Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
              <div style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <p style={{ fontSize: '32px', fontWeight: '700', color: '#2563eb', margin: 0 }}>{reportData.totalRegistrations.toLocaleString()}</p>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0' }}>Total Registrations</p>
              </div>
              <div style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <p style={{ fontSize: '32px', fontWeight: '700', color: '#059669', margin: 0 }}>{reportData.sessionsAnalyzed.toLocaleString()}</p>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0' }}>Sessions Analyzed</p>
              </div>
              <div style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <p style={{ fontSize: '32px', fontWeight: '700', color: '#dc2626', margin: 0 }}>{(reportData.debug?.skippedBTParent || 0) + (reportData.debug?.skippedOtherChild || 0)}</p>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0' }}>Bundle Registrations Excluded</p>
              </div>
            </div>

            {reportData.bundleRules && (
              <div style={{ backgroundColor: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#854d0e', margin: '0 0 12px' }}>Bundle Handling Summary</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '13px' }}>
                  <div style={{ backgroundColor: '#fffbeb', padding: '12px', borderRadius: '6px', border: '1px solid #fde68a' }}>
                    <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '4px' }}>Basic Training Bundles</div>
                    <div style={{ color: '#78350f' }}>Rule: {reportData.bundleRules.basicTrainingBundles.rule}</div>
                    <div style={{ color: '#78350f' }}>Parent sessions: {reportData.bundleRules.basicTrainingBundles.parentSessionCount}</div>
                    <div style={{ color: '#78350f' }}>Child sessions counted: {reportData.bundleRules.basicTrainingBundles.childSessionCount}</div>
                  </div>
                  <div style={{ backgroundColor: '#fffbeb', padding: '12px', borderRadius: '6px', border: '1px solid #fde68a' }}>
                    <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '4px' }}>Other Bundles</div>
                    <div style={{ color: '#78350f' }}>Rule: {reportData.bundleRules.otherBundles.rule}</div>
                    <div style={{ color: '#78350f' }}>Types: {reportData.bundleRules.otherBundles.types}</div>
                    <div style={{ color: '#78350f' }}>Parent sessions counted: {reportData.bundleRules.otherBundles.parentSessionCount}</div>
                    <div style={{ color: '#78350f' }}>Child sessions excluded: {reportData.bundleRules.otherBundles.childSessionCount}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Debug Stats */}
            {reportData.debug && (
              <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#475569', margin: '0 0 12px' }}>Debug: Processing Breakdown</h4>
                <div style={{ fontSize: '13px', marginBottom: '8px', padding: '8px', backgroundColor: '#f1f5f9', borderRadius: '4px' }}>
                  <div><span style={{ color: '#6b7280' }}>Table used:</span> <strong>{reportData.debug.regTableName}</strong></div>
                  <div><span style={{ color: '#6b7280' }}>Session field:</span> <strong>{reportData.debug.sessionLookupField}</strong></div>
                  <div><span style={{ color: '#6b7280' }}>Student field:</span> <strong>{reportData.debug.studentLookupField}</strong></div>
                  <div style={{ wordBreak: 'break-all' }}><span style={{ color: '#6b7280' }}>Fields found:</span> <span style={{ fontSize: '11px' }}>{reportData.debug.regTableFields}</span></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', fontSize: '13px' }}>
                  <div><span style={{ color: '#6b7280' }}>Fetched:</span> <strong>{reportData.debug.totalFetched?.toLocaleString()}</strong></div>
                  <div><span style={{ color: '#6b7280' }}>No Session Match:</span> <strong>{reportData.debug.skippedNoSession?.toLocaleString()}</strong></div>
                  <div><span style={{ color: '#6b7280' }}>BT Parent Skipped:</span> <strong>{reportData.debug.skippedBTParent?.toLocaleString()}</strong></div>
                  <div><span style={{ color: '#6b7280' }}>Other Bundle Child:</span> <strong>{reportData.debug.skippedOtherChild?.toLocaleString()}</strong></div>
                  <div><span style={{ color: '#6b7280' }}>Status Filtered:</span> <strong>{reportData.debug.skippedStatus?.toLocaleString()}</strong></div>
                  <div><span style={{ color: '#6b7280' }}>Year Out of Range:</span> <strong>{reportData.debug.skippedYear?.toLocaleString()}</strong></div>
                </div>
                {reportData.debug.statusCounts && Object.keys(reportData.debug.statusCounts).length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>Registration Status Values Found: </span>
                    <span style={{ fontSize: '13px' }}>
                      {Object.entries(reportData.debug.statusCounts).map(([status, count]) => `${status} (${count})`).join(', ')}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Main Report Table */}
            <div style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', marginBottom: '24px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#1e293b' }}>
                    <th style={{ padding: '14px 20px', textAlign: 'left', color: '#ffffff', fontSize: '14px', fontWeight: '600' }}>Region</th>
                    {years.map(year => (
                      <th key={year} style={{ padding: '14px 20px', textAlign: 'right', color: '#ffffff', fontSize: '14px', fontWeight: '600' }}>{year}</th>
                    ))}
                    <th style={{ padding: '14px 20px', textAlign: 'right', color: '#ffffff', fontSize: '14px', fontWeight: '600' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {regions.map((region, idx) => {
                    const rowTotal = years.reduce((sum, year) => sum + (reportData.data[year]?.[region] || 0), 0);
                    return (
                      <tr key={region} style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '12px 20px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>{region}</td>
                        {years.map(year => (
                          <td key={year} style={{ padding: '12px 20px', textAlign: 'right', fontSize: '14px', color: '#374151' }}>
                            {(reportData.data[year]?.[region] || 0).toLocaleString()}
                          </td>
                        ))}
                        <td style={{ padding: '12px 20px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{rowTotal.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                  {/* Total Row */}
                  <tr style={{ backgroundColor: '#1e293b' }}>
                    <td style={{ padding: '14px 20px', fontSize: '14px', fontWeight: '700', color: '#ffffff' }}>Total</td>
                    {years.map(year => (
                      <td key={year} style={{ padding: '14px 20px', textAlign: 'right', fontSize: '14px', fontWeight: '700', color: '#ffffff' }}>
                        {(reportData.data[year]?.Total || 0).toLocaleString()}
                      </td>
                    ))}
                    <td style={{ padding: '14px 20px', textAlign: 'right', fontSize: '14px', fontWeight: '700', color: '#ffffff' }}>
                      {reportData.totalRegistrations.toLocaleString()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Location Mapping Debug Section */}
            <div style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
              <button
                onClick={() => setShowLocationMapping(!showLocationMapping)}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  backgroundColor: '#f9fafb',
                  border: 'none',
                  borderBottom: showLocationMapping ? '1px solid #e5e7eb' : 'none',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>Location → Region Mapping ({Object.keys(reportData.locationRegionMap || {}).length} locations)</span>
                <span>{showLocationMapping ? '▼' : '▶'}</span>
              </button>
              {showLocationMapping && reportData.locationRegionMap && (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f3f4f6' }}>
                      <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#6b7280' }}>Location</th>
                      <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#6b7280' }}>Assigned Region</th>
                      <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#6b7280' }}>Campus</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(reportData.locationRegionMap)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([location, info], idx) => (
                        <tr key={location} style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb', borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '8px 20px', fontSize: '13px', color: '#374151' }}>{location}</td>
                          <td style={{ padding: '8px 20px', fontSize: '13px' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '9999px',
                              fontSize: '12px',
                              fontWeight: '500',
                              backgroundColor: info.region === 'Washington' ? '#dbeafe' : info.region.includes('Bootcamp') ? '#e0e7ff' : info.region === 'South' ? '#fef3c7' : info.region === 'MI East' ? '#fce7f3' : info.region === 'Online' ? '#d1fae5' : '#f3f4f6',
                              color: info.region === 'Washington' ? '#1e40af' : info.region.includes('Bootcamp') ? '#3730a3' : info.region === 'South' ? '#92400e' : info.region === 'MI East' ? '#9d174d' : info.region === 'Online' ? '#065f46' : '#374151'
                            }}>
                              {info.region}
                            </span>
                          </td>
                          <td style={{ padding: '8px 20px', fontSize: '13px', color: '#6b7280' }}>{info.campus || '—'}</td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default RegistrationReport;
